import type { ExecutionUnitType, ParsedDocument } from "@magic-md/core";
import type { LocaleParams } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import { JsonTree } from "@magic-md/plugin-sdk";
import { Plus, Trash2, X } from "lucide-react";
import {
    type FormEvent,
    type MouseEvent as ReactMouseEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { cn } from "../../lib/utils";
import { useEditorStore } from "../../store/editor-store";
import { useOutputWatchStore, summarizeWatchValue } from "../../store/output-watch-store";
import type { OutputTracePoint, OutputWatch } from "../../store/output-watch-store";
import { usePluginExecutionStore } from "../../store/plugin-execution-store";
import { frontendLog } from "../../utils/frontend-logger";

interface UnitCandidate {
    id: string;
    unitType: ExecutionUnitType;
    output?: unknown;
}

interface WatchSuggestion {
    value: string;
    label: string;
    detail: string;
    kind: "unit" | "scope" | "field";
}

type EditorT = (key: string, params?: LocaleParams) => string;

type DiffLine = {
    kind: "context" | "added" | "removed";
    text: string;
};

interface SequenceTimeline {
    width: number;
    positionsByPointId: Map<string, number>;
}

const MAX_DIFF_LINES = 260;
const WATCH_COLUMN_DEFAULT_WIDTH = 176;
const WATCH_COLUMN_MIN_WIDTH = 120;
const WATCH_COLUMN_MAX_WIDTH = 360;
const DETAIL_PANEL_DEFAULT_WIDTH = 448;
const DETAIL_PANEL_MIN_WIDTH = 320;
const DETAIL_PANEL_MIN_MAIN_WIDTH = 420;
const TRACE_POINT_GAP = 30;
const TRACE_POINT_PADDING = 24;
const TRACE_MIN_WIDTH = 560;

function clampNumber(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}

function getDetailPanelMaxWidth(container: HTMLDivElement | null): number {
    const containerWidth = container?.clientWidth && container.clientWidth > 0
        ? container.clientWidth
        : typeof window === "undefined"
            ? 1024
            : window.innerWidth;
    return Math.max(DETAIL_PANEL_MIN_WIDTH, containerWidth - DETAIL_PANEL_MIN_MAIN_WIDTH);
}

function startHorizontalResize(
    event: ReactMouseEvent,
    onMove: (deltaX: number) => void,
): void {
    event.preventDefault();
    const startX = event.clientX;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    const onMouseMove = (moveEvent: MouseEvent) => {
        onMove(moveEvent.clientX - startX);
    };
    const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
}

function formatTime(epochMs: number): string {
    const date = new Date(epochMs);
    return (
        date.toLocaleTimeString("en-GB", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }) +
        "." +
        String(date.getMilliseconds()).padStart(3, "0")
    );
}

function pointClassName(point: OutputTracePoint, selected: boolean): string {
    return cn(
        "absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border transition-transform hover:scale-125",
        point.eventKind === "failed"
            ? "border-destructive bg-destructive"
            : point.eventKind === "partial"
                ? "border-info bg-background"
                : "border-success bg-success",
        selected && "ring-2 ring-primary ring-offset-1 ring-offset-background scale-125",
    );
}

function stringifyForDiff(value: unknown): string {
    if (value === undefined) return "undefined";
    if (typeof value === "string") return JSON.stringify(value);
    try {
        const json = JSON.stringify(value, null, 2);
        return json ?? String(value);
    } catch {
        return String(value);
    }
}

function clampDiffLines(lines: DiffLine[]): DiffLine[] {
    if (lines.length <= MAX_DIFF_LINES) return lines;
    const head = Math.floor(MAX_DIFF_LINES / 2);
    const tail = MAX_DIFF_LINES - head - 1;
    return [
        ...lines.slice(0, head),
        { kind: "context", text: "... diff truncated ..." },
        ...lines.slice(lines.length - tail),
    ];
}

function buildFallbackDiff(previousLines: string[], nextLines: string[]): DiffLine[] {
    let prefix = 0;
    while (
        prefix < previousLines.length &&
        prefix < nextLines.length &&
        previousLines[prefix] === nextLines[prefix]
    ) {
        prefix += 1;
    }

    let previousEnd = previousLines.length - 1;
    let nextEnd = nextLines.length - 1;
    while (
        previousEnd >= prefix &&
        nextEnd >= prefix &&
        previousLines[previousEnd] === nextLines[nextEnd]
    ) {
        previousEnd -= 1;
        nextEnd -= 1;
    }

    return clampDiffLines([
        ...previousLines.slice(0, prefix).map((text) => ({ kind: "context" as const, text })),
        ...previousLines.slice(prefix, previousEnd + 1).map((text) => ({ kind: "removed" as const, text })),
        ...nextLines.slice(prefix, nextEnd + 1).map((text) => ({ kind: "added" as const, text })),
        ...nextLines.slice(nextEnd + 1).map((text) => ({ kind: "context" as const, text })),
    ]);
}

function buildLineDiff(previousValue: unknown, nextValue: unknown): DiffLine[] {
    const nextLines = stringifyForDiff(nextValue).split("\n");
    if (previousValue === undefined) {
        return clampDiffLines(nextLines.map((text) => ({ kind: "added", text })));
    }

    const previousLines = stringifyForDiff(previousValue).split("\n");
    if (previousLines.join("\n") === nextLines.join("\n")) {
        return clampDiffLines(nextLines.map((text) => ({ kind: "context", text })));
    }

    if (previousLines.length * nextLines.length > 40_000) {
        return buildFallbackDiff(previousLines, nextLines);
    }

    const table = Array.from(
        { length: previousLines.length + 1 },
        () => new Array<number>(nextLines.length + 1).fill(0),
    );
    for (let i = previousLines.length - 1; i >= 0; i -= 1) {
        for (let j = nextLines.length - 1; j >= 0; j -= 1) {
            table[i][j] = previousLines[i] === nextLines[j]
                ? table[i + 1][j + 1] + 1
                : Math.max(table[i + 1][j], table[i][j + 1]);
        }
    }

    const lines: DiffLine[] = [];
    let i = 0;
    let j = 0;
    while (i < previousLines.length && j < nextLines.length) {
        if (previousLines[i] === nextLines[j]) {
            lines.push({ kind: "context", text: previousLines[i] });
            i += 1;
            j += 1;
        } else if (table[i + 1][j] >= table[i][j + 1]) {
            lines.push({ kind: "removed", text: previousLines[i] });
            i += 1;
        } else {
            lines.push({ kind: "added", text: nextLines[j] });
            j += 1;
        }
    }
    while (i < previousLines.length) {
        lines.push({ kind: "removed", text: previousLines[i] });
        i += 1;
    }
    while (j < nextLines.length) {
        lines.push({ kind: "added", text: nextLines[j] });
        j += 1;
    }

    return clampDiffLines(lines);
}

function computeSequenceTimeline(points: OutputTracePoint[]): SequenceTimeline {
    const positionsByPointId = new Map<string, number>();
    points.forEach((point, index) => {
        positionsByPointId.set(point.id, TRACE_POINT_PADDING + index * TRACE_POINT_GAP);
    });
    const width = Math.max(
        TRACE_MIN_WIDTH,
        TRACE_POINT_PADDING * 2 + Math.max(0, points.length - 1) * TRACE_POINT_GAP,
    );
    return { width, positionsByPointId };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function readPathValue(root: unknown, path: string): unknown {
    const trimmed = path.trim().replace(/^\./, "").replace(/\.$/, "");
    if (!trimmed) return root;
    let current = root;
    for (const segment of trimmed.split(".").filter(Boolean)) {
        if (current == null) return undefined;
        if (Array.isArray(current)) {
            const index = Number(segment);
            if (!Number.isInteger(index)) return undefined;
            current = current[index];
        } else if (typeof current === "object") {
            current = (current as Record<string, unknown>)[segment];
        } else {
            return undefined;
        }
    }
    return current;
}

function inferUseOutput(entry: ReturnType<typeof usePluginExecutionStore.getState>["useExecutions"] extends Map<string, infer T> ? T : never): unknown {
    return {
        materializedId: entry.materializedId,
        sourceName: entry.sourceName,
        kind: entry.kind,
        ...(entry.materializedBody ? { materializedBody: entry.materializedBody } : {}),
    };
}

function addCandidate(
    map: Map<string, UnitCandidate>,
    candidate: UnitCandidate,
): void {
    const existing = map.get(candidate.id);
    if (!existing || (candidate.output !== undefined && existing.output === undefined)) {
        map.set(candidate.id, candidate);
    }
}

function collectUnitCandidates(
    parsedDocument: ParsedDocument | null,
    executions: ReturnType<typeof usePluginExecutionStore.getState>["executions"],
    forExecutions: ReturnType<typeof usePluginExecutionStore.getState>["forExecutions"],
    contentExecutions: ReturnType<typeof usePluginExecutionStore.getState>["contentExecutions"],
    importExecutions: ReturnType<typeof usePluginExecutionStore.getState>["importExecutions"],
    useExecutions: ReturnType<typeof usePluginExecutionStore.getState>["useExecutions"],
): UnitCandidate[] {
    const candidates = new Map<string, UnitCandidate>();

    for (const [id, definition] of parsedDocument?.definitions ?? []) {
        addCandidate(candidates, {
            id,
            unitType: "definition",
            output: executions.get(id)?.output ?? definition.output,
        });
    }
    for (const [id, forDefinition] of parsedDocument?.forDefinitions ?? []) {
        const execution = forExecutions.get(id);
        addCandidate(candidates, {
            id,
            unitType: "for",
            output: execution
                ? { iterations: execution.iterations, meta: execution.meta }
                : forDefinition.output,
        });
    }
    for (const [id, contentDefinition] of parsedDocument?.contentDefinitions ?? []) {
        addCandidate(candidates, {
            id,
            unitType: "content",
            output: contentExecutions.get(id)?.output ?? contentDefinition.output,
        });
    }
    for (const binding of parsedDocument?.imports ?? []) {
        const execution = importExecutions.get(binding.namespace);
        addCandidate(candidates, {
            id: binding.namespace,
            unitType: "import",
            output: execution
                ? {
                    symbols: execution.symbols,
                    ...(execution.document ? { document: execution.document } : {}),
                }
                : binding.output
                    ? {
                        symbols: binding.output.symbols,
                        ...(binding.output.document ? { document: binding.output.document } : {}),
                    }
                    : undefined,
        });
    }
    for (const binding of parsedDocument?.uses ?? []) {
        const execution = useExecutions.get(binding.id);
        addCandidate(candidates, {
            id: binding.id,
            unitType: "use",
            output: execution ? inferUseOutput(execution) : binding.output,
        });
    }

    for (const [id, entry] of executions) {
        addCandidate(candidates, { id, unitType: "definition", output: entry.output });
    }
    for (const [id, entry] of forExecutions) {
        addCandidate(candidates, { id, unitType: "for", output: { iterations: entry.iterations, meta: entry.meta } });
    }
    for (const [id, entry] of contentExecutions) {
        addCandidate(candidates, { id, unitType: "content", output: entry.output });
    }
    for (const [id, entry] of importExecutions) {
        addCandidate(candidates, {
            id,
            unitType: "import",
            output: {
                symbols: entry.symbols,
                ...(entry.document ? { document: entry.document } : {}),
            },
        });
    }
    for (const [id, entry] of useExecutions) {
        addCandidate(candidates, { id, unitType: "use", output: inferUseOutput(entry) });
    }

    return [...candidates.values()].sort((a, b) => a.id.localeCompare(b.id));
}

function stripRefWrapper(expression: string): string {
    const trimmed = expression.trim();
    if (trimmed.startsWith("$ref{") && trimmed.endsWith("}")) {
        return trimmed.slice(5, -1).trim();
    }
    return trimmed;
}

function findExpressionCandidate(
    expression: string,
    candidates: UnitCandidate[],
): { candidate: UnitCandidate; rest: string } | null {
    const normalized = stripRefWrapper(expression);
    const sorted = [...candidates].sort((a, b) => b.id.length - a.id.length);
    for (const candidate of sorted) {
        if (normalized === candidate.id) return { candidate, rest: "" };
        if (normalized.startsWith(`${candidate.id}.`)) {
            return {
                candidate,
                rest: normalized.slice(candidate.id.length + 1),
            };
        }
    }
    return null;
}

function parseWatchExpression(
    expression: string,
    candidates: UnitCandidate[],
): { unitId: string; path: string } {
    const normalized = stripRefWrapper(expression);
    const match = findExpressionCandidate(normalized, candidates);
    if (match) {
        return {
            unitId: match.candidate.id,
            path: match.rest || "output",
        };
    }
    const firstDot = normalized.indexOf(".");
    if (firstDot === -1) return { unitId: normalized, path: "output" };
    return {
        unitId: normalized.slice(0, firstDot),
        path: normalized.slice(firstDot + 1) || "output",
    };
}

function appendDot(value: string): string {
    return value.endsWith(".") ? value : `${value}.`;
}

function buildFieldSuggestions(
    candidate: UnitCandidate,
    rest: string,
    t: EditorT,
): WatchSuggestion[] {
    const firstDot = rest.indexOf(".");
    const scope = firstDot === -1 ? rest : rest.slice(0, firstDot);
    const tail = firstDot === -1 ? "" : rest.slice(firstDot + 1);
    const lowerScope = scope.toLowerCase();

    if (firstDot === -1 && scope !== "output") {
        return ["output"]
            .filter((item) => item.startsWith(lowerScope))
            .map((item) => ({
                value: appendDot(`${candidate.id}.${item}`),
                label: item,
                detail: t("watch.suggestion.valueRoot"),
                kind: "scope" as const,
            }));
    }

    if (scope !== "output" || candidate.output === undefined) return [];

    const tailWithoutTrailingDot = tail.endsWith(".") ? tail.slice(0, -1) : tail;
    const lastDot = tailWithoutTrailingDot.lastIndexOf(".");
    const parentPath = tail.endsWith(".")
        ? tailWithoutTrailingDot
        : lastDot === -1
            ? ""
            : tailWithoutTrailingDot.slice(0, lastDot);
    const prefix = tail.endsWith(".")
        ? ""
        : lastDot === -1
            ? tailWithoutTrailingDot
            : tailWithoutTrailingDot.slice(lastDot + 1);
    const parent = readPathValue(candidate.output, parentPath);
    const basePath = parentPath ? `${parentPath}.` : "";
    const lowerPrefix = prefix.toLowerCase();

    if (Array.isArray(parent)) {
        return parent
            .slice(0, 8)
            .map((_, index) => String(index))
            .filter((item) => item.startsWith(lowerPrefix))
            .map((item) => ({
                value: `${candidate.id}.output.${basePath}${item}`,
                label: item,
                detail: t("watch.suggestion.arrayIndex"),
                kind: "field" as const,
            }));
    }

    if (!isRecord(parent)) return [];

    return Object.keys(parent)
        .filter((key) => key.toLowerCase().includes(lowerPrefix))
        .slice(0, 12)
        .map((key) => ({
            value: isRecord(parent[key]) || Array.isArray(parent[key])
                ? appendDot(`${candidate.id}.output.${basePath}${key}`)
                : `${candidate.id}.output.${basePath}${key}`,
            label: key,
            detail: summarizeWatchValue(parent[key]),
            kind: "field" as const,
        }));
}

function buildWatchSuggestions(
    expression: string,
    candidates: UnitCandidate[],
    t: EditorT,
): WatchSuggestion[] {
    const normalized = stripRefWrapper(expression);
    if (!normalized.includes(".")) {
        const q = normalized.toLowerCase();
        return candidates
            .filter((candidate) => candidate.id.toLowerCase().includes(q))
            .slice(0, 12)
            .map((candidate) => ({
                value: appendDot(candidate.id),
                label: candidate.id,
                detail: candidate.unitType,
                kind: "unit" as const,
            }));
    }

    const match = findExpressionCandidate(normalized, candidates);
    if (!match) return [];
    return buildFieldSuggestions(match.candidate, match.rest, t);
}

function AddWatchForm({
    sessionId,
    documentPath,
    unitCandidates,
    onClose,
}: {
    sessionId: string | null;
    documentPath: string | null;
    unitCandidates: UnitCandidate[];
    onClose: () => void;
}) {
    const t = useT("editor");
    const addWatch = useOutputWatchStore((state) => state.addWatch);
    const [expression, setExpression] = useState("");
    const [includePartial, setIncludePartial] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const suggestions = useMemo(
        () => buildWatchSuggestions(expression, unitCandidates, t),
        [expression, t, unitCandidates],
    );

    const applySuggestion = (suggestion: WatchSuggestion) => {
        setExpression(suggestion.value);
        setSuggestionsOpen(true);
        setActiveSuggestionIndex(0);
    };

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const run = async () => {
            setSubmitting(true);
            try {
                const parsed = parseWatchExpression(expression, unitCandidates);
                if (!parsed.unitId) {
                    throw new Error(t("watch.error.requiresUnitExpression"));
                }
                const targetSessionId =
                    sessionId ?? await usePluginExecutionStore.getState()._ensureSession();
                if (!targetSessionId) {
                    throw new Error(t("watch.error.noActiveSession"));
                }
                addWatch({
                    sessionId: targetSessionId,
                    documentPath: documentPath ?? undefined,
                    unitId: parsed.unitId,
                    path: parsed.path,
                    includePartial,
                });
                setExpression("");
                setIncludePartial(false);
                setSuggestionsOpen(false);
                setError(null);
                onClose();
            } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
            } finally {
                setSubmitting(false);
            }
        };
        void run();
    };

    return (
        <form
            className="flex min-w-0 items-center gap-1 border-l border-border/50 pl-2"
            onSubmit={submit}
        >
            <div className="relative">
                <input
                    className="h-6 w-72 rounded border border-border bg-background px-2 text-[11px] font-mono outline-none focus:border-primary"
                    value={expression}
                    onChange={(event) => {
                        setExpression(event.target.value);
                        setSuggestionsOpen(true);
                        setActiveSuggestionIndex(0);
                    }}
                    onFocus={() => setSuggestionsOpen(true)}
                    onBlur={() => {
                        window.setTimeout(() => setSuggestionsOpen(false), 120);
                    }}
                    onKeyDown={(event) => {
                        if (!suggestionsOpen || suggestions.length === 0) return;
                        if (event.key === "ArrowDown") {
                            event.preventDefault();
                            setActiveSuggestionIndex((index) => (index + 1) % suggestions.length);
                        } else if (event.key === "ArrowUp") {
                            event.preventDefault();
                            setActiveSuggestionIndex((index) => (index - 1 + suggestions.length) % suggestions.length);
                        } else if (event.key === "Tab") {
                            event.preventDefault();
                            applySuggestion(suggestions[activeSuggestionIndex]);
                        } else if (event.key === "Enter") {
                            event.preventDefault();
                            applySuggestion(suggestions[activeSuggestionIndex]);
                        } else if (event.key === "Escape") {
                            setSuggestionsOpen(false);
                        }
                    }}
                    placeholder={t("watch.expressionPlaceholder")}
                    aria-label={t("watch.expressionLabel")}
                    autoComplete="off"
                />
                {suggestionsOpen && suggestions.length > 0 ? (
                    <div
                        className="absolute left-0 top-7 z-50 max-h-56 w-80 overflow-auto rounded-md border border-border bg-background py-1 shadow-lg"
                        role="listbox"
                    >
                        {suggestions.map((suggestion, index) => (
                            <button
                                key={`${suggestion.kind}:${suggestion.value}`}
                                type="button"
                                data-testid={`watch-suggestion:${suggestion.value}`}
                                className={cn(
                                    "flex w-full items-center gap-2 px-2 py-1 text-left text-[11px]",
                                    index === activeSuggestionIndex
                                        ? "bg-muted text-foreground"
                                        : "text-foreground/80 hover:bg-muted/50",
                                )}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => applySuggestion(suggestion)}
                            >
                                <span className="min-w-0 flex-1 truncate font-mono">
                                    {suggestion.label}
                                </span>
                                <span className="shrink-0 truncate text-[10px] text-muted-foreground/60">
                                    {suggestion.detail}
                                </span>
                            </button>
                        ))}
                    </div>
                ) : null}
            </div>
            <label className="flex h-6 items-center gap-1 text-[10px] text-muted-foreground">
                <input
                    type="checkbox"
                    checked={includePartial}
                    onChange={(event) => setIncludePartial(event.target.checked)}
                />
                {t("watch.includePartial")}
            </label>
            <button
                type="submit"
                className="h-6 rounded bg-primary px-2 text-[10px] font-medium text-primary-foreground disabled:opacity-50"
                disabled={submitting}
            >
                {submitting ? t("watch.adding") : t("watch.add")}
            </button>
            <button
                type="button"
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={onClose}
                aria-label={t("watch.closeAdd")}
                title={t("watch.closeAdd")}
            >
                <X size={12} />
            </button>
            {error ? (
                <span className="max-w-48 truncate text-[10px] text-destructive" title={error}>
                    {error}
                </span>
            ) : null}
        </form>
    );
}

function WatchRow({
    watch,
    points,
    timeline,
    watchColumnWidth,
    selected,
    selectedPointId,
    onSelectWatch,
    onSelectPoint,
}: {
    watch: OutputWatch;
    points: OutputTracePoint[];
    timeline: SequenceTimeline;
    watchColumnWidth: number;
    selected: boolean;
    selectedPointId: string | null;
    onSelectWatch: (watchId: string) => void;
    onSelectPoint: (pointId: string) => void;
}) {
    const t = useT("editor");
    const removeWatch = useOutputWatchStore((state) => state.removeWatch);

    return (
        <div
            className={cn(
                "group flex h-9 min-w-0 border-b border-border/30 hover:bg-muted/10",
                selected && "bg-primary/5",
            )}
            data-output-watch-id={watch.id}
        >
            <div
                className="flex shrink-0 items-center gap-2 border-r border-border/40 px-2"
                data-testid="output-watch-label-column"
                style={{ width: watchColumnWidth }}
            >
                <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left font-mono text-[11px] text-foreground/85"
                    onClick={() => onSelectWatch(watch.id)}
                    title={watch.hasLastValue ? summarizeWatchValue(watch.lastValue) : watch.label}
                >
                    {watch.label}
                </button>
                <button
                    type="button"
                    className="shrink-0 rounded p-1 text-muted-foreground/50 opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                    onClick={() => removeWatch(watch.id)}
                    aria-label={t("watch.removeAria", { label: watch.label })}
                    title={t("watch.remove")}
                >
                    <Trash2 size={11} />
                </button>
            </div>
            <div
                className="relative min-w-0 flex-1"
                data-testid="output-watch-sequence-track"
                style={{ minWidth: timeline.width }}
            >
                <div className="absolute left-0 right-0 top-1/2 border-t border-border/30" />
                {points.map((point) => {
                    const left = timeline.positionsByPointId.get(point.id) ?? TRACE_POINT_PADDING;
                    return (
                        <button
                            key={point.id}
                            type="button"
                            data-testid={`output-watch-point:${point.id}`}
                            className={pointClassName(point, point.id === selectedPointId)}
                            style={{ left }}
                            onClick={() => onSelectPoint(point.id)}
                            title={t("watch.pointTitle", {
                                time: formatTime(point.timestamp),
                                summary: point.diffSummary,
                            })}
                            aria-label={t("watch.inspectPoint", {
                                label: point.label,
                                time: formatTime(point.timestamp),
                            })}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function DetailPanel({
    point,
    width,
    onResizeMouseDown,
    onResizeStep,
    onCollapse,
}: {
    point: OutputTracePoint;
    width: number;
    onResizeMouseDown: (event: ReactMouseEvent<HTMLButtonElement>) => void;
    onResizeStep: (delta: number) => void;
    onCollapse: () => void;
}) {
    const t = useT("editor");
    const [activeDetailTab, setActiveDetailTab] = useState<"diff" | "values" | "meta">("diff");
    const diffLines = useMemo(
        () => buildLineDiff(point.previousValue, point.value),
        [point.previousValue, point.value],
    );

    useEffect(() => {
        setActiveDetailTab("diff");
    }, [point.id]);

    return (
        <div
            className="relative flex h-full shrink-0 flex-col border-l border-border bg-background"
            data-testid="output-watch-detail-panel"
            style={{ width }}
        >
            <button
                type="button"
                className="absolute bottom-0 left-0 top-0 z-10 w-2 -translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-primary/20 focus-visible:bg-primary/20"
                onMouseDown={onResizeMouseDown}
                onKeyDown={(event) => {
                    if (event.key === "ArrowLeft") {
                        event.preventDefault();
                        onResizeStep(12);
                    } else if (event.key === "ArrowRight") {
                        event.preventDefault();
                        onResizeStep(-12);
                    }
                }}
                aria-label={t("watch.resizeDetails")}
                title={t("watch.resizeDetailsTitle")}
            />
            <div className="border-b border-border/50 px-3 py-2">
                <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="truncate font-mono text-[12px] font-semibold text-foreground">
                            {point.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground/60">
                            {point.diffSummary}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={onCollapse}
                        aria-label={t("watch.collapseDetails")}
                        title={t("watch.collapseDetailsTitle")}
                    >
                        <X size={12} />
                    </button>
                </div>
            </div>
            <div className="flex h-8 shrink-0 items-end gap-1 border-b border-border/50 px-2 pt-1">
                {(
                    [
                        ["diff", t("watch.detailTab.diff")],
                        ["values", t("watch.detailTab.values")],
                        ["meta", t("watch.detailTab.meta")],
                    ] as const
                ).map(([id, label]) => (
                    <button
                        key={id}
                        type="button"
                        className={cn(
                            "h-7 rounded-t border border-b-0 px-2 text-[10px] font-medium",
                            activeDetailTab === id
                                ? "border-border bg-background text-foreground"
                                : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                        )}
                        onClick={() => setActiveDetailTab(id)}
                        role="tab"
                        aria-selected={activeDetailTab === id}
                    >
                        {label}
                    </button>
                ))}
            </div>
            <div className="flex-1 overflow-auto p-3 text-[11px]">
                {activeDetailTab === "diff" ? (
                    <div className="space-y-3">
                        <section className="rounded border border-primary/20 bg-primary/5 p-2">
                            <div className="mb-1 text-[10px] font-semibold uppercase text-primary/80">
                                {t("watch.detail.change")}
                            </div>
                            <div className="text-[12px] font-medium text-foreground">
                                {point.diffSummary}
                            </div>
                            <div className="mt-1 font-mono text-[10px] text-muted-foreground/60">
                                {formatTime(point.timestamp)} / {point.eventKind}
                            </div>
                        </section>

                        {point.error ? (
                            <section className="rounded border border-destructive/30 bg-destructive/5 p-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase text-destructive/80">
                                    {t("common.error")}
                                </div>
                                <pre className="whitespace-pre-wrap break-words font-mono text-[10px] text-destructive">
                                    {point.error}
                                </pre>
                            </section>
                        ) : null}

                        <section className="overflow-hidden rounded border border-border/50 bg-muted/10">
                            <div className="border-b border-border/40 px-2 py-1 text-[10px] font-semibold uppercase text-muted-foreground/60">
                                {t("watch.detail.diff")}
                            </div>
                            <div className="max-h-[28rem] overflow-auto py-1 font-mono text-[10px]">
                                {diffLines.map((line, index) => (
                                    <div
                                        key={`${index}:${line.kind}:${line.text}`}
                                        className={cn(
                                            "grid grid-cols-[18px_1fr] gap-1 px-2 leading-5",
                                            line.kind === "added" && "bg-success/10 text-success",
                                            line.kind === "removed" && "bg-destructive/10 text-destructive",
                                            line.kind === "context" && "text-muted-foreground/75",
                                        )}
                                    >
                                        <span className="select-none text-right">
                                            {line.kind === "added" ? "+" : line.kind === "removed" ? "-" : " "}
                                        </span>
                                        <span className="whitespace-pre break-all">
                                            {line.text || " "}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </div>
                ) : activeDetailTab === "values" ? (
                    <div className="space-y-3">
                        <section className="rounded border border-border/40 bg-muted/10 p-2">
                            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground/60">
                                {t("watch.detail.currentValue")}
                            </div>
                            <JsonTree value={point.value} defaultExpandDepth={1} />
                        </section>

                        {point.previousValue !== undefined ? (
                            <section className="rounded border border-border/40 bg-muted/10 p-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase text-muted-foreground/60">
                                    {t("watch.detail.previousValue")}
                                </div>
                                <JsonTree value={point.previousValue} defaultExpandDepth={1} />
                            </section>
                        ) : null}
                    </div>
                ) : (
                    <div className="grid grid-cols-[72px_1fr] gap-x-2 gap-y-1">
                        <span className="text-muted-foreground/60">{t("watch.meta.unit")}</span>
                        <code className="truncate font-mono">{point.unitId}</code>
                        <span className="text-muted-foreground/60">{t("common.type")}</span>
                        <span>{point.unitType}</span>
                        <span className="text-muted-foreground/60">{t("watch.meta.path")}</span>
                        <code className="truncate font-mono">{point.path}</code>
                        <span className="text-muted-foreground/60">{t("watch.meta.event")}</span>
                        <span>{point.eventKind}</span>
                        <span className="text-muted-foreground/60">{t("log.field.time")}</span>
                        <code className="font-mono">{formatTime(point.timestamp)}</code>
                        <span className="text-muted-foreground/60">{t("watch.detail.diff")}</span>
                        <span>{point.diffSummary}</span>
                        {point.meta?.actionName ? (
                            <>
                                <span className="text-muted-foreground/60">{t("common.action")}</span>
                                <span>{point.meta.actionName}</span>
                            </>
                        ) : null}
                        {point.meta?.duration != null ? (
                            <>
                                <span className="text-muted-foreground/60">{t("common.duration")}</span>
                                <span>{point.meta.duration}ms</span>
                            </>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}

export function OutputWatchPanel() {
    const t = useT("editor");
    const activeTabId = useEditorStore((state) => state.activeTabId);
    const activeDocumentPath = useEditorStore((state) =>
        state.tabs.find((tab) => tab.id === state.activeTabId)?.filePath ?? null
    );
    const parsedDocument = useEditorStore((state) => state.parsedDocument);
    const directSessionId = usePluginExecutionStore((state) => state.sessionId);
    const tabSessionMap = usePluginExecutionStore((state) => state.tabSessionMap);
    const executions = usePluginExecutionStore((state) => state.executions);
    const forExecutions = usePluginExecutionStore((state) => state.forExecutions);
    const contentExecutions = usePluginExecutionStore((state) => state.contentExecutions);
    const importExecutions = usePluginExecutionStore((state) => state.importExecutions);
    const useExecutions = usePluginExecutionStore((state) => state.useExecutions);
    const sessionId = directSessionId ?? (activeTabId ? tabSessionMap.get(activeTabId) ?? null : null);
    const canAddWatch = !!sessionId || !!activeTabId;
    const watches = useOutputWatchStore((state) => state.watches);
    const points = useOutputWatchStore((state) => state.points);
    const selectedWatchId = useOutputWatchStore((state) => state.selectedWatchId);
    const selectedPointId = useOutputWatchStore((state) => state.selectedPointId);
    const clearPoints = useOutputWatchStore((state) => state.clearPoints);
    const hydrateDocumentWatches = useOutputWatchStore((state) => state.hydrateDocumentWatches);
    const setSelectedWatch = useOutputWatchStore((state) => state.setSelectedWatch);
    const setSelectedPoint = useOutputWatchStore((state) => state.setSelectedPoint);
    const [adding, setAdding] = useState(false);
    const [detailOpen, setDetailOpen] = useState(true);
    const [watchColumnWidth, setWatchColumnWidth] = useState(WATCH_COLUMN_DEFAULT_WIDTH);
    const [detailPanelWidth, setDetailPanelWidth] = useState(DETAIL_PANEL_DEFAULT_WIDTH);
    const panelRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const unitCandidates = useMemo(
        () => collectUnitCandidates(
            parsedDocument,
            executions,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
        ),
        [
            parsedDocument,
            executions,
            forExecutions,
            contentExecutions,
            importExecutions,
            useExecutions,
        ],
    );

    const visibleWatches = useMemo(
        () => watches.filter((watch) => {
            if (watch.documentPath) {
                return !activeDocumentPath || watch.documentPath === activeDocumentPath;
            }
            return !sessionId || watch.sessionId === sessionId;
        }),
        [activeDocumentPath, sessionId, watches],
    );
    const visibleWatchIds = useMemo(
        () => new Set(visibleWatches.map((watch) => watch.id)),
        [visibleWatches],
    );
    const visiblePoints = useMemo(
        () => points.filter((point) => visibleWatchIds.has(point.watchId)),
        [points, visibleWatchIds],
    );
    const pointsByWatch = useMemo(() => {
        const grouped = new Map<string, OutputTracePoint[]>();
        for (const point of visiblePoints) {
            const existing = grouped.get(point.watchId) ?? [];
            existing.push(point);
            grouped.set(point.watchId, existing);
        }
        return grouped;
    }, [visiblePoints]);
    const timeline = useMemo(() => computeSequenceTimeline(visiblePoints), [visiblePoints]);
    const selectedPoint = useMemo(
        () => visiblePoints.find((point) => point.id === selectedPointId) ?? null,
        [selectedPointId, visiblePoints],
    );
    const selectWatch = (watchId: string) => {
        setSelectedWatch(watchId);
    };
    const selectPoint = (pointId: string) => {
        const point = points.find((item) => item.id === pointId);
        if (point) setSelectedWatch(point.watchId);
        setSelectedPoint(pointId);
        setDetailOpen(true);
    };
    const resizeWatchColumn = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
        const startWidth = watchColumnWidth;
        startHorizontalResize(event, (deltaX) => {
            setWatchColumnWidth(clampNumber(
                startWidth + deltaX,
                WATCH_COLUMN_MIN_WIDTH,
                WATCH_COLUMN_MAX_WIDTH,
            ));
        });
    }, [watchColumnWidth]);
    const resizeDetailPanel = useCallback((event: ReactMouseEvent<HTMLButtonElement>) => {
        const startWidth = detailPanelWidth;
        const maxWidth = getDetailPanelMaxWidth(panelRef.current);
        startHorizontalResize(event, (deltaX) => {
            setDetailPanelWidth(clampNumber(
                startWidth - deltaX,
                DETAIL_PANEL_MIN_WIDTH,
                maxWidth,
            ));
        });
    }, [detailPanelWidth]);
    const stepWatchColumn = (delta: number) => {
        setWatchColumnWidth((width) => clampNumber(
            width + delta,
            WATCH_COLUMN_MIN_WIDTH,
            WATCH_COLUMN_MAX_WIDTH,
        ));
    };
    const stepDetailPanel = (delta: number) => {
        setDetailPanelWidth((width) => clampNumber(
            width + delta,
            DETAIL_PANEL_MIN_WIDTH,
            getDetailPanelMaxWidth(panelRef.current),
        ));
    };

    useEffect(() => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }, [visiblePoints.length]);

    useEffect(() => {
        if (!activeDocumentPath) return;
        void hydrateDocumentWatches({
            documentPath: activeDocumentPath,
            sessionId: sessionId ?? undefined,
        }).catch((err) => {
            frontendLog.warn("Failed to hydrate output watches", err, {
                source: "system",
            });
        });
    }, [activeDocumentPath, hydrateDocumentWatches, sessionId]);

    useEffect(() => {
        if (!selectedWatchId || !scrollRef.current) return;
        const target = Array.from(
            scrollRef.current.querySelectorAll<HTMLElement>("[data-output-watch-id]"),
        ).find((element) => element.dataset.outputWatchId === selectedWatchId);
        if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
    }, [selectedWatchId, visibleWatches.length]);

    return (
        <div ref={panelRef} className="flex min-h-0 flex-1" data-testid="output-watch-panel">
            <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border bg-muted/10 px-2">
                    <button
                        type="button"
                        className="flex h-6 items-center gap-1 rounded border border-border bg-background px-2 text-[10px] font-medium text-foreground hover:bg-muted/50 disabled:opacity-50"
                        onClick={() => setAdding(true)}
                        disabled={!canAddWatch}
                        title={canAddWatch ? t("watch.addWatch") : t("watch.noActiveDocument")}
                    >
                        <Plus size={12} />
                        {t("watch.addButton")}
                    </button>
                    <button
                        type="button"
                        className="flex h-6 items-center gap-1 rounded px-2 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                        onClick={clearPoints}
                    >
                        <Trash2 size={12} />
                        {t("common.clear")}
                    </button>
                    <div className="h-4 w-px bg-border/60" />
                    <span className="text-[10px] text-muted-foreground/60">
                        {t("watch.counts", {
                            watches: visibleWatches.length,
                            points: visiblePoints.length,
                        })}
                    </span>
                    {selectedPoint && !detailOpen ? (
                        <button
                            type="button"
                            className="flex h-6 items-center gap-1 rounded px-2 text-[10px] text-muted-foreground hover:bg-muted hover:text-foreground"
                            onClick={() => setDetailOpen(true)}
                        >
                            {t("watch.details")}
                        </button>
                    ) : null}
                    {adding ? (
                        <AddWatchForm
                            sessionId={sessionId}
                            documentPath={activeDocumentPath}
                            unitCandidates={unitCandidates}
                            onClose={() => setAdding(false)}
                        />
                    ) : null}
                </div>

                <div className="flex h-6 shrink-0 border-b border-border/50 bg-background text-[10px] text-muted-foreground/60">
                    <div
                        className="relative flex shrink-0 items-center border-r border-border/40 px-3"
                        data-testid="output-watch-label-header"
                        style={{ width: watchColumnWidth }}
                    >
                        {t("watch.column")}
                        <button
                            type="button"
                            className="absolute bottom-0 right-0 top-0 w-2 translate-x-1/2 cursor-col-resize bg-transparent transition-colors hover:bg-primary/20 focus-visible:bg-primary/20"
                            onMouseDown={resizeWatchColumn}
                            onKeyDown={(event) => {
                                if (event.key === "ArrowLeft") {
                                    event.preventDefault();
                                    stepWatchColumn(-12);
                                } else if (event.key === "ArrowRight") {
                                    event.preventDefault();
                                    stepWatchColumn(12);
                                }
                            }}
                            aria-label={t("watch.resizeLabelColumn")}
                            title={t("watch.resizeLabelColumnTitle")}
                        />
                    </div>
                    <div
                        className="relative min-w-0 flex-1"
                        style={{ minWidth: timeline.width }}
                    >
                        {visiblePoints.length > 0 ? (
                            <>
                                <span className="absolute left-2 top-1/2 -translate-y-1/2">
                                    {t("watch.older")}
                                </span>
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono">
                                    {t("watch.newer")}
                                </span>
                            </>
                        ) : (
                            <span className="absolute left-2 top-1/2 -translate-y-1/2">
                                {t("watch.timelineHelp")}
                            </span>
                        )}
                    </div>
                </div>

                <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
                    {visibleWatches.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-[11px] text-muted-foreground/50">
                            {t("watch.empty")}
                        </div>
                    ) : (
                        visibleWatches.map((watch) => (
                            <WatchRow
                                key={watch.id}
                                watch={watch}
                                points={pointsByWatch.get(watch.id) ?? []}
                                timeline={timeline}
                                watchColumnWidth={watchColumnWidth}
                                selected={watch.id === selectedWatchId}
                                selectedPointId={selectedPointId}
                                onSelectWatch={selectWatch}
                                onSelectPoint={selectPoint}
                            />
                        ))
                    )}
                </div>
            </div>
            {selectedPoint && detailOpen ? (
                <DetailPanel
                    point={selectedPoint}
                    width={detailPanelWidth}
                    onResizeMouseDown={resizeDetailPanel}
                    onResizeStep={stepDetailPanel}
                    onCollapse={() => setDetailOpen(false)}
                />
            ) : null}
        </div>
    );
}
